import { useEffect, useState } from "react";
import { Copy, KeyRound, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";

export default function AdminCoachesTab() {
  const [coaches, setCoaches] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [issued, setIssued] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [c, a] = await Promise.all([api.get("/admin/coaches"), api.get("/admin/admins")]);
      setCoaches(c.data); setAdmins(a.data);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const addCoach = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/coaches", form);
      toast.success("Coach login created");
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (e2) { toast.error(fmtErr(e2)); }
    finally { setBusy(false); }
  };

  const issue = async (email) => {
    try {
      const { data } = await api.post(`/admin/coaches/${encodeURIComponent(email)}/reset-password`);
      setIssued(data); toast.success("Temporary coach password generated"); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const deactivate = async (email) => {
    if (!window.confirm(`Deactivate coach access for ${email}?`)) return;
    try { await api.delete(`/admin/coaches/${encodeURIComponent(email)}`); toast.success("Coach deactivated"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="admin-coaches" className="grid lg:grid-cols-[1fr_400px] gap-4">
      <section className="border border-zinc-800 bg-zinc-950">
        <div className="p-6 border-b border-zinc-800">
          <h2 className="font-display uppercase text-4xl">Coach access</h2>
          <p className="text-sm text-zinc-500 mt-2">Admins can create coach IDs, set passwords, reset access or deactivate a coach.</p>
        </div>
        {coaches.map((c) => (
          <div key={c.email} data-testid={`admin-coach-${c.email}`} className="p-5 border-b border-zinc-800/70 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="font-bold text-zinc-100">{c.name} <span className="ml-2 text-[9px] uppercase tracking-[0.14em] px-2 py-1 bg-zinc-800 text-zinc-400">{c.configured ? "Configured" : "Admin added"}</span></p>
              <p className="text-xs text-zinc-500">{c.email}</p>
              <p className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mt-2">{c.active ? "Active" : "Inactive"} · {c.credential_issued ? "Password issued" : "No password yet"}{c.must_change_password ? " · change required" : ""}</p>
            </div>
            <div className="flex gap-2">
              <button data-testid={`issue-coach-password-${c.email}`} onClick={() => issue(c.email)}
                className="inline-flex items-center gap-2 border border-volt text-volt px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-bold hover:bg-volt hover:text-black transition-colors">
                <KeyRound size={14} /> {c.credential_issued ? "Reset" : "Issue"}
              </button>
              {c.active && <button data-testid={`deactivate-coach-${c.email}`} onClick={() => deactivate(c.email)}
                className="border border-zinc-700 px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-bold text-zinc-500 hover:border-red-400 hover:text-red-400 transition-colors">Deactivate</button>}
            </div>
          </div>
        ))}
      </section>

      <aside className="space-y-4">
        <form onSubmit={addCoach} data-testid="add-coach-form" className="border border-zinc-800 bg-zinc-950 p-5">
          <h3 className="font-display uppercase text-3xl">Add coach login</h3>
          <input data-testid="add-coach-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Coach name"
            className="mt-5 w-full bg-ink border border-zinc-700 px-4 py-3 text-sm focus:border-volt outline-none" />
          <input data-testid="add-coach-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="Coach email ID"
            className="mt-3 w-full bg-ink border border-zinc-700 px-4 py-3 text-sm focus:border-volt outline-none" />
          <input data-testid="add-coach-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} placeholder="Coach password"
            className="mt-3 w-full bg-ink border border-zinc-700 px-4 py-3 text-sm focus:border-volt outline-none" />
          <button data-testid="add-coach-submit" disabled={busy}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-volt text-black uppercase tracking-[0.16em] text-xs font-extrabold py-4 hover:bg-white disabled:opacity-50 transition-colors">
            <UserPlus size={15} /> {busy ? "Creating…" : "Create coach login"}
          </button>
        </form>

        {issued && (
          <div data-testid="issued-coach-password" className="border border-volt bg-volt/10 p-5">
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-bold">Temporary password — shown once</p>
            <p className="font-mono text-lg text-volt break-all mt-3">{issued.temporary_password}</p>
            <button data-testid="copy-coach-password" onClick={() => { navigator.clipboard.writeText(issued.temporary_password); toast.success("Copied"); }}
              className="mt-4 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] font-bold text-zinc-300 hover:text-volt"><Copy size={13} /> Copy</button>
          </div>
        )}

        <div className="border border-zinc-800 bg-zinc-950 p-5">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-4">Registered admins</p>
          {admins.map((a) => (
            <div key={a.email} data-testid={`registered-admin-${a.email}`} className="py-3 border-b border-zinc-800/60 last:border-0">
              <p className="text-sm text-zinc-200 break-all">{a.email}</p>
              <p className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">{a.primary ? "Primary admin" : "Backup admin"}</p>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}
